import { Fragment, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, PageHeader, Pill, Table, Td, Th } from "@/components/ui";

interface Me {
  telegram_id: number;
}

interface TimeseriesPoint {
  date: string;
  users: number;
  payments_count: number;
  revenue: number;
  new_count: number;
  new_revenue: number;
  renewal_count: number;
  renewal_revenue: number;
  tracking_revenue: number;
  referral_revenue: number;
  organic_revenue: number;
}

interface Timeseries {
  days: TimeseriesPoint[];
}

interface Stats {
  users_total: number;
  users_today: number;
  users_this_month: number;
  accesses_active: number;
  accesses_test: number;
  accesses_paid: number;
  subs_proxy_active: number;
  subs_vpn_active: number;
  revenue_proxy_total: number;
  revenue_vpn_total: number;
  revenue_proxy_stars: number;
  revenue_vpn_stars: number;
  revenue_today: number;
  revenue_week: number;
  revenue_total: number;
  revenue_today_stars: number;
  revenue_week_stars: number;
  revenue_total_stars: number;
  new_count: number;
  new_revenue: number;
  renewal_count: number;
  renewal_revenue: number;
  new_count_week: number;
  new_revenue_week: number;
  renewal_count_week: number;
  renewal_revenue_week: number;
  new_count_month: number;
  new_revenue_month: number;
  renewal_count_month: number;
  renewal_revenue_month: number;
  tracking_clicks: number;
  tracking_signups: number;
  tracking_paid: number;
  tracking_revenue: number;
  referral_total: number;
  referral_paid: number;
  referral_revenue: number;
}

interface InfraSummary {
  nodes: { total: number; up: number; degraded: number; offline: number };
  hosts: { total: number; up: number; degraded: number; down: number; undeployed: number };
  conns: number;
  rx_bps: number;
  tx_bps: number;
  agents_online: number;
}

function fmtBpsShort(bps?: number): string {
  if (!bps) return "0";
  const bits = bps * 8;
  if (bits >= 1e6) return `${(bits / 1e6).toFixed(1)}M`;
  if (bits >= 1e3) return `${Math.round(bits / 1e3)}K`;
  return `${bits}`;
}

interface UserListItem {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  created_at: string;
}

interface TrackLinkLite {
  id: number;
  name: string;
  revenue: number;
  paid_signups: number;
}

interface ReferrerLite {
  id: number;
  username: string | null;
  first_name: string | null;
  revenue: number;
  paid_refs: number;
}

interface RecentPaymentsItem {
  id: number;
  amount: number;
  currency: string;
  created_at: string;
  user_id: number;
  user_username: string | null;
  user_telegram_id: number;
}

interface TopReferrerItem {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  referrals_count: number;
}

const rub = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  maximumFractionDigits: 0,
});

function formatPaymentAmount(amount: number, currency: string): string {
  // XTR = Telegram Stars. Их отрисовываем как «50 ⭐️» (не валютой Intl
  // через ISO-код — звезда не валютный знак).
  if (currency === "XTR") {
    return `${amount.toLocaleString("ru-RU")} ⭐️`;
  }
  return rub.format(amount);
}

const RANGES = [
  { days: 1, label: "24ч" },
  { days: 7, label: "7д" },
  { days: 30, label: "30д" },
  { days: 90, label: "90д" },
  { days: 180, label: "180д" },
];

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

export default function Dashboard() {
  const [rangeDays, setRangeDays] = useState(30);

  const me = useQuery<Me>({ queryKey: ["me"], queryFn: () => api<Me>("/api/admin/me") });
  const stats = useQuery<Stats>({
    queryKey: ["stats"],
    queryFn: () => api<Stats>("/api/admin/stats"),
  });
  const infra = useQuery<InfraSummary>({
    queryKey: ["infra-summary"],
    queryFn: () => api<InfraSummary>("/api/admin/infra-summary"),
    refetchInterval: 8000,
  });
  const timeseries = useQuery<Timeseries>({
    queryKey: ["stats", "timeseries", rangeDays],
    queryFn: () => api<Timeseries>(`/api/admin/stats/timeseries?days=${rangeDays}`),
    placeholderData: keepPreviousData,
  });
  const recentUsers = useQuery<{ users: UserListItem[] }>({
    queryKey: ["dashboard", "recent-users"],
    queryFn: () => api("/api/admin/users?limit=10&sort=created_at&direction=desc"),
  });
  const recentPayments = useQuery<{ items: RecentPaymentsItem[] }>({
    queryKey: ["dashboard", "recent-payments"],
    queryFn: () => api("/api/admin/dashboard/recent-payments?limit=10"),
  });
  const topReferrers = useQuery<{ items: TopReferrerItem[] }>({
    queryKey: ["dashboard", "top-referrers"],
    queryFn: () => api("/api/admin/dashboard/top-referrers?limit=10"),
  });
  const trackingLinks = useQuery<{ items: TrackLinkLite[] }>({
    queryKey: ["dashboard", "tracking-links"],
    queryFn: () => api("/api/admin/tracking-links"),
  });
  const referralTop = useQuery<{ items: ReferrerLite[] }>({
    queryKey: ["dashboard", "referral-top"],
    queryFn: () => api("/api/admin/referral-stats?limit=50"),
  });

  const s = stats.data;
  const topLinks = [...(trackingLinks.data?.items ?? [])]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 3);
  const topRefs = [...(referralTop.data?.items ?? [])]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 3);

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Дашборд"
        right={
          <div className="text-zinc-400 text-sm">
            {me.data && `tg_id=${me.data.telegram_id}`}
          </div>
        }
      />

      {s && (
        <>
          <SectionHeader>Обзор</SectionHeader>
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              label="Пользователи"
              value={s.users_total.toLocaleString("ru-RU")}
              sub={`+${s.users_today} сегодня · +${s.users_this_month} за месяц`}
            />
            <StatCard
              label="Прокси-подписки"
              value={(s.subs_proxy_active || s.accesses_paid).toLocaleString("ru-RU")}
              tone="emerald"
              sub="активные платные прокси"
              extra={
                <div className="mt-3 pt-3 border-t border-white/[0.06]">
                  <div className="text-2xl font-bold text-sky-300 tabular-nums">
                    {s.subs_vpn_active.toLocaleString("ru-RU")} 🛡
                  </div>
                  <div className="text-zinc-500 text-sm mt-1">активные VPN-подписки</div>
                </div>
              }
            />
            <StatCard
              label="Активных тестов"
              value={s.accesses_test.toLocaleString("ru-RU")}
              tone="amber"
              sub="сейчас на 48-часовом тесте"
            />
            <StatCard
              label="Выручка · всего"
              value={rub.format(s.revenue_total)}
              glow
              sub={`неделя ${rub.format(s.revenue_week)} · сегодня ${rub.format(s.revenue_today)}`}
              extra={
                <div className="mt-3 pt-3 border-t border-white/[0.06]">
                  <div className="text-2xl font-bold text-amber-300 tabular-nums">
                    {s.revenue_total_stars.toLocaleString("ru-RU")} ⭐️
                  </div>
                  <div className="text-zinc-500 text-sm mt-1">
                    Telegram Stars · неделя {s.revenue_week_stars.toLocaleString("ru-RU")}⭐️ · сегодня{" "}
                    {s.revenue_today_stars.toLocaleString("ru-RU")}⭐️
                  </div>
                  <div className="text-zinc-500 text-xs mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>прокси {rub.format(s.revenue_proxy_total)}{s.revenue_proxy_stars ? ` +${s.revenue_proxy_stars}⭐️` : ""}</span>
                    <span className="text-sky-300/80">VPN {rub.format(s.revenue_vpn_total)}{s.revenue_vpn_stars ? ` +${s.revenue_vpn_stars}⭐️` : ""}</span>
                  </div>
                </div>
              }
            />
          </section>

          {infra.data && (
            <>
              <SectionHeader
                right={<span className="text-[10px] text-zinc-600">{infra.isFetching ? "…" : "live"} · обновляется</span>}
              >
                Инфраструктура
              </SectionHeader>
              <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
                <InfraTile label="Ноды работают" value={`${infra.data.nodes.up}/${infra.data.nodes.total}`} tone="emerald" />
                <InfraTile label="Ноды проблемы" value={infra.data.nodes.degraded} tone={infra.data.nodes.degraded ? "rose" : "default"} />
                <InfraTile label="Хосты работают" value={`${infra.data.hosts.up}/${infra.data.hosts.total}`} tone="emerald" />
                <InfraTile label="Хосты деград/лежат" value={infra.data.hosts.degraded + infra.data.hosts.down} tone={infra.data.hosts.degraded + infra.data.hosts.down ? "rose" : "default"} />
                <InfraTile label="Коннектов сейчас" value={infra.data.conns.toLocaleString("ru-RU")} tone="brand" />
                <InfraTile label="Трафик" value={`↓${fmtBpsShort(infra.data.rx_bps)} ↑${fmtBpsShort(infra.data.tx_bps)}`} />
              </section>
            </>
          )}

          {timeseries.data && (
            <>
              <SectionHeader
                right={
                  <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    {RANGES.map((r) => (
                      <button
                        key={r.days}
                        onClick={() => setRangeDays(r.days)}
                        className={`h-8 px-3.5 rounded-lg text-sm font-medium transition-all ${
                          rangeDays === r.days
                            ? "bg-brand-soft text-brand glow-ring"
                            : "text-zinc-400 hover:text-zinc-200"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                }
              >
                Динамика
              </SectionHeader>
              <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                <Card title={`Выручка (${rangeDays}д)`} className="lg:col-span-2">
                  <ChartArea
                    data={timeseries.data.days}
                    dataKey="revenue"
                    color="#2dd4bf"
                    formatter={(v) => rub.format(v)}
                    breakdown
                    tall
                  />
                </Card>
                <Card title={`Регистрации (${rangeDays}д)`}>
                  <ChartArea data={timeseries.data.days} dataKey="users" color="#22d3ee" tall />
                </Card>
              </section>

              <SectionHeader>Разбивка</SectionHeader>
              <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                <Card title={`Выручка по источникам (${rangeDays}д)`} className="lg:col-span-2">
                  <SourceChart data={timeseries.data.days} />
                </Card>
                <Card title="Оплаты: новые / продления">
                  <div className="grid grid-cols-[auto_1fr_1fr] gap-x-5 gap-y-2 text-sm items-baseline">
                    <div />
                    <div className="text-emerald-300 text-xs uppercase tracking-wide">🆕 Новые</div>
                    <div className="text-sky-300 text-xs uppercase tracking-wide">🔁 Продл.</div>
                    {([
                      ["Неделя", s.new_count_week, s.new_revenue_week, s.renewal_count_week, s.renewal_revenue_week],
                      ["Месяц", s.new_count_month, s.new_revenue_month, s.renewal_count_month, s.renewal_revenue_month],
                      ["Всего", s.new_count, s.new_revenue, s.renewal_count, s.renewal_revenue],
                    ] as [string, number, number, number, number][]).map(([label, nc, nr, rc, rr]) => (
                      <Fragment key={label}>
                        <div className="text-zinc-500 text-xs">{label}</div>
                        <div>
                          <span className="font-semibold text-emerald-300 tabular-nums">{nc}</span>
                          <span className="text-zinc-500"> · {rub.format(nr)}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-sky-300 tabular-nums">{rc}</span>
                          <span className="text-zinc-500"> · {rub.format(rr)}</span>
                        </div>
                      </Fragment>
                    ))}
                  </div>
                  <div className="text-zinc-600 text-xs mt-3">первая покупка vs повторная (RUB)</div>
                </Card>
              </section>
            </>
          )}

          {s && (
            <>
              <SectionHeader>Каналы привлечения</SectionHeader>
              <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
              <Card title="Трекинг-ссылки (итого)">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <MiniStat label="Переходы" value={s.tracking_clicks.toLocaleString("ru-RU")} />
                  <MiniStat label="Регистрации" value={s.tracking_signups.toLocaleString("ru-RU")} />
                  <MiniStat label="Покупки" value={s.tracking_paid.toLocaleString("ru-RU")} accent="emerald" />
                  <MiniStat label="Выручка" value={rub.format(s.tracking_revenue)} accent="emerald" />
                </div>
                <div className="text-xs text-zinc-500 mb-2">Топ по выручке</div>
                {topLinks.length === 0 && <EmptyHint />}
                {topLinks.map((l) => (
                  <TopRow key={l.id} name={l.name} sub={`${l.paid_signups} покупок`} value={rub.format(l.revenue)} />
                ))}
                <Link to="/referrals" className="text-xs text-brand hover:underline">все трекинг-ссылки →</Link>
              </Card>

              <Card title="Рефералка (итого)">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <MiniStat label="Привлечено" value={s.referral_total.toLocaleString("ru-RU")} />
                  <MiniStat label="Купили" value={s.referral_paid.toLocaleString("ru-RU")} accent="emerald" />
                  <MiniStat label="Выручка" value={rub.format(s.referral_revenue)} accent="emerald" />
                </div>
                <div className="text-xs text-zinc-500 mb-2">Топ по выручке</div>
                {topRefs.length === 0 && <EmptyHint />}
                {topRefs.map((u) => (
                  <TopRow
                    key={u.id}
                    to={`/users/${u.id}`}
                    name={u.username ? `@${u.username}` : u.first_name ?? `#${u.id}`}
                    sub={`${u.paid_refs} купили`}
                    value={rub.format(u.revenue)}
                  />
                ))}
                <Link to="/referrals" className="text-xs text-brand hover:underline">вся реферальная статистика →</Link>
              </Card>
            </section>
            </>
          )}

          <SectionHeader>Активность</SectionHeader>
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card title="Новые юзеры">
              {(recentUsers.data?.users ?? []).length === 0 ? (
                <EmptyHint />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>id</Th>
                      <Th>username</Th>
                      <Th>дата</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recentUsers.data?.users ?? []).map((u) => (
                      <tr key={u.id}>
                        <Td>
                          <Link to={`/users/${u.id}`} className="text-brand hover:underline">
                            #{u.id}
                          </Link>
                        </Td>
                        <Td>
                          {u.username ? (
                            <Link
                              to={`/users/${u.id}`}
                              className="text-zinc-100 hover:text-brand hover:underline"
                            >
                              @{u.username}
                            </Link>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </Td>
                        <Td className="text-zinc-500">{shortDate(u.created_at)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>

            <Card title="Последние покупки">
              {(recentPayments.data?.items ?? []).length === 0 ? (
                <EmptyHint />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>юзер</Th>
                      <Th>сумма</Th>
                      <Th>дата</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recentPayments.data?.items ?? []).map((p) => (
                      <tr key={p.id}>
                        <Td>
                          <Link
                            to={`/users/${p.user_id}`}
                            className={
                              p.user_username
                                ? "text-zinc-100 hover:text-brand hover:underline"
                                : "text-brand hover:underline"
                            }
                          >
                            {p.user_username ? `@${p.user_username}` : `#${p.user_id}`}
                          </Link>
                        </Td>
                        <Td className="text-emerald-300">{formatPaymentAmount(p.amount, p.currency)}</Td>
                        <Td className="text-zinc-500">{shortDate(p.created_at)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>

            <Card title="Топ по рефералам">
              {(topReferrers.data?.items ?? []).length === 0 ? (
                <EmptyHint />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>юзер</Th>
                      <Th>привёл</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(topReferrers.data?.items ?? []).map((r) => (
                      <tr key={r.id}>
                        <Td>
                          <Link
                            to={`/users/${r.id}`}
                            className={
                              r.username
                                ? "text-zinc-100 hover:text-brand hover:underline"
                                : "text-brand hover:underline"
                            }
                          >
                            {r.username ? `@${r.username}` : `#${r.id}`}
                          </Link>
                        </Td>
                        <Td>
                          <Pill tone="success">{r.referrals_count}</Pill>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </section>
        </>
      )}
    </div>
  );
}

function EmptyHint() {
  return <div className="text-zinc-600 text-sm py-3">Пусто.</div>;
}

function StatCard({
  label,
  value,
  sub,
  tone = "default",
  glow = false,
  extra,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "emerald" | "amber" | "brand";
  glow?: boolean;
  extra?: React.ReactNode;
}) {
  const toneColor =
    tone === "emerald"
      ? "text-emerald-300"
      : tone === "amber"
        ? "text-amber-300"
        : tone === "brand"
          ? "text-brand"
          : "text-zinc-50";
  return (
    <div className={`relative rounded-2xl card-elev p-5 overflow-hidden ${glow ? "glow-ring" : ""}`}>
      {glow && (
        <div className="pointer-events-none absolute -right-10 -top-10 w-32 h-32 rounded-full bg-brand/20 blur-3xl" />
      )}
      <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-2">{label}</div>
      <div className={`text-3xl font-bold tabular-nums tracking-tight ${toneColor}`}>{value}</div>
      {sub && <div className="text-zinc-500 text-sm mt-1.5">{sub}</div>}
      {extra}
    </div>
  );
}

function InfraTile({ label, value, tone = "default" }: { label: string; value: string | number; tone?: "default" | "emerald" | "rose" | "brand" | "amber" }) {
  const c =
    tone === "emerald" ? "text-emerald-300"
    : tone === "rose" ? "text-rose-300"
    : tone === "brand" ? "text-brand"
    : tone === "amber" ? "text-amber-300"
    : "text-zinc-100";
  return (
    <div className="rounded-xl card-elev px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">{label}</div>
      <div className={`text-xl font-bold tabular-nums mt-0.5 ${c}`}>{value}</div>
    </div>
  );
}

function SectionHeader({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
      <div className="flex items-center gap-2.5">
        <span className="w-1 h-4 rounded-full bg-brand-grad" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
          {children}
        </span>
      </div>
      {right}
    </div>
  );
}

function MiniStat({ label, value, accent }: { label: string; value: string; accent?: "emerald" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-zinc-600 mb-0.5">{label}</div>
      <div className={`text-lg font-semibold ${accent === "emerald" ? "text-emerald-300" : "text-zinc-100"}`}>
        {value}
      </div>
    </div>
  );
}

function TopRow({ name, sub, value, to }: { name: string; sub: string; value: string; to?: string }) {
  const title = to ? (
    <Link to={to} className="text-zinc-200 hover:text-brand truncate">{name}</Link>
  ) : (
    <span className="text-zinc-200 truncate">{name}</span>
  );
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-zinc-800/50 last:border-0">
      <div className="min-w-0">
        <div className="text-sm truncate">{title}</div>
        <div className="text-xs text-zinc-600">{sub}</div>
      </div>
      <div className="text-sm text-emerald-300 font-medium shrink-0">{value}</div>
    </div>
  );
}

function SourceChart({ data }: { data: TimeseriesPoint[] }) {
  const short = (iso: string) => {
    const [, m, d] = iso.split("-");
    return `${d}.${m}`;
  };
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e2430" />
          <XAxis dataKey="date" tickFormatter={short} tick={{ fill: "#64748b", fontSize: 11 }} stroke="#1e2430" />
          <YAxis tick={{ fill: "#64748b", fontSize: 11 }} stroke="#1e2430" tickFormatter={(v) => rub.format(Number(v))} width={80} />
          <Tooltip
            contentStyle={{ backgroundColor: "#0f1218", border: "1px solid rgba(148,163,184,0.18)", borderRadius: 8, fontSize: 12 }}
            labelStyle={{ color: "#e6e9ef" }}
            formatter={(v: any, n: any) => [rub.format(Number(v)), n]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area type="monotone" dataKey="organic_revenue" name="Органика" stackId="s" stroke="#64748b" fill="#64748b" fillOpacity={0.45} />
          <Area type="monotone" dataKey="tracking_revenue" name="Трек-ссылки" stackId="s" stroke="#a78bfa" fill="#a78bfa" fillOpacity={0.5} />
          <Area type="monotone" dataKey="referral_revenue" name="Рефералы" stackId="s" stroke="#34d399" fill="#34d399" fillOpacity={0.5} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function RevenueTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload as TimeseriesPoint;
  return (
    <div style={{ background: "#0f1218", border: "1px solid rgba(148,163,184,0.18)", borderRadius: 8, padding: "8px 10px", fontSize: 12 }}>
      <div style={{ color: "#e6e9ef", marginBottom: 4 }}>{label}</div>
      <div style={{ color: "#e6e9ef" }}>Выручка: {rub.format(p.revenue)}</div>
      <div style={{ color: "#34d399", marginTop: 2 }}>🆕 Новые: {p.new_count} · {rub.format(p.new_revenue)}</div>
      <div style={{ color: "#38bdf8" }}>🔁 Продления: {p.renewal_count} · {rub.format(p.renewal_revenue)}</div>
    </div>
  );
}

interface ChartAreaProps {
  data: TimeseriesPoint[];
  dataKey: "users" | "revenue" | "payments_count";
  color: string;
  formatter?: (v: number) => string;
  breakdown?: boolean;
  tall?: boolean;
}

function ChartArea({ data, dataKey, color, formatter, breakdown, tall }: ChartAreaProps) {
  const short = (iso: string) => {
    const [, m, d] = iso.split("-");
    return `${d}.${m}`;
  };
  return (
    <div className={tall ? "h-72" : "h-56"}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.4} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e2430" />
          <XAxis
            dataKey="date"
            tickFormatter={short}
            tick={{ fill: "#64748b", fontSize: 11 }}
            stroke="#1e2430"
          />
          <YAxis
            tick={{ fill: "#64748b", fontSize: 11 }}
            stroke="#1e2430"
            tickFormatter={(v) => (formatter ? formatter(v) : String(v))}
            width={formatter ? 80 : 40}
          />
          {breakdown ? (
            <Tooltip content={<RevenueTooltip />} />
          ) : (
            <Tooltip
              contentStyle={{
                backgroundColor: "#0f1218",
                border: "1px solid rgba(148,163,184,0.18)",
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: "#e6e9ef" }}
              formatter={(v) => (formatter ? formatter(Number(v)) : String(v))}
            />
          )}
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            fill={`url(#grad-${dataKey})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
